import geometry from "./bart-geometry.json";
import { distanceMeters } from "../shared/time";
import type { Coordinates } from "../shared/types";

export const BART_YELLOW_STATIONS = [
	"PITT", "NCON", "CONC", "PHIL", "WCRK", "LAFY", "ORIN", "ROCK", "MCAR", "19TH", "12TH", "WOAK",
	"EMBR", "MONT", "POWL", "CIVC", "16TH", "24TH", "GLEN", "BALB", "DALY", "COLM", "SSAN", "SBRN", "SFIA", "MLBR",
] as const;

const segments: Readonly<Record<string, readonly number[][]>> = geometry.segments;
const trackStations = new Map(Object.entries(segments).flatMap(([key, points]) => {
	const [from, to] = key.split(":");
	return [[from, { lat: points[0][0], lon: points[0][1] }], [to, { lat: points[points.length - 1][0], lon: points[points.length - 1][1] }]] as const;
}));

function adjacentPath(fromId: string, toId: string): Coordinates[] | null {
	const points = segments[[fromId, toId].sort().join(":")];
	if (!points) return null;
	const path = points.map(([lat, lon]) => ({ lat, lon }));
	if (fromId > toId) path.reverse();
	return path;
}

/** Published GTFS alignment, in travel order; skipped Yellow-line predictions retain the bends. */
export function bartTrackPath(fromId: string, toId: string): Coordinates[] | null {
	if (fromId === toId) {
		const station = trackStations.get(fromId);
		return station ? [station] : null;
	}
	const direct = adjacentPath(fromId, toId);
	if (direct) return direct;
	const from = BART_YELLOW_STATIONS.findIndex(id => id === fromId);
	const to = BART_YELLOW_STATIONS.findIndex(id => id === toId);
	if (from < 0 || to < 0) return null;
	const step = from < to ? 1 : -1;
	const path: Coordinates[] = [];
	for (let index = from; index !== to; index += step) {
		const segment = adjacentPath(BART_YELLOW_STATIONS[index], BART_YELLOW_STATIONS[index + step]);
		if (!segment) return null;
		path.push(...(path.length ? segment.slice(1) : segment));
	}
	return path;
}

export function bartLinePaths(stationIds: readonly string[]): Coordinates[][] {
	return stationIds.slice(1).flatMap((id, index) => {
		const path = bartTrackPath(stationIds[index], id);
		return path ? [path] : [];
	});
}

/** Distance along the track, not a straight chord between platforms. Still only an estimate. */
export function bartTrackPosition(fromId: string, toId: string, fraction: number): Coordinates | null {
	if (!Number.isFinite(fraction)) return null;
	const path = bartTrackPath(fromId, toId);
	if (!path?.length) return null;
	if (fraction <= 0 || path.length === 1) return path[0];
	if (fraction >= 1) return path[path.length - 1];
	const lengths = path.slice(1).map((point, index) => distanceMeters(path[index], point));
	let remaining = lengths.reduce((sum, length) => sum + length, 0) * fraction;
	for (let index = 0; index < lengths.length; index++) {
		const length = lengths[index];
		if (remaining <= length && length > 0) {
			const part = remaining / length, from = path[index], to = path[index + 1];
			return { lat: from.lat + (to.lat - from.lat) * part, lon: from.lon + (to.lon - from.lon) * part };
		}
		remaining -= length;
	}
	return path[path.length - 1];
}
